import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import { useState } from 'react';
import { CommonHelper } from 'src/utils/commonHelper';
import { Box, Button, Card, Chip, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import FacebookAccounts from './facebookAccounts.component';
import InstagramAccounts from './instagramAccounts.component';
import WhatsAppAccounts from './whatsappAccounts.component';

export default function IntegratedAccounts({
  whatsAppLoader,
  facebookLoader,
  instagramLoader,
  connectedProfiles = [],
  connectedProfilesLoader = false,
  onWhatsAppConnect,
  onWhatsAppReauthenticate,
  onFacebookConnect,
  onInstagramConnect,
  onFacebookReauthenticate,
  onInstagramReauthenticate,
  onRefresh,
  refreshingAccount,
  onDelete,
  onToggleEnable,
}) {
  useTranslation();
  const [accountTab, setAccountTab] = useState('whatsapp');
  const whatsAppAccounts = connectedProfiles.filter((account) => account.service_name === 'whatsapp');
  const facebookAccounts = connectedProfiles.filter((account) => account.service_name === 'facebook');
  const instagramAccounts = connectedProfiles.filter((account) => account.service_name === 'instagram');
  const inactiveCount = [...whatsAppAccounts, ...facebookAccounts, ...instagramAccounts].filter(
    (account) => account.is_active === false
  ).length;

  return (
    <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 1 }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Integrated Accounts
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                Connected WhatsApp numbers, Facebook pages, and Instagram profiles are available in their own chat
                pages.
              </Typography>
              {inactiveCount > 0 && (
                <Chip
                  size="small"
                  color="error"
                  label={`${inactiveCount} inactive account${inactiveCount > 1 ? 's' : ''}`}
                />
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={onInstagramConnect} disabled={instagramLoader}>
              {instagramLoader ? <CircularProgress color="inherit" size={18} /> : 'Connect Instagram'}
            </Button>
            <Button variant="outlined" onClick={onFacebookConnect} disabled={facebookLoader}>
              {facebookLoader ? <CircularProgress color="inherit" size={18} /> : 'Add Facebook Page'}
            </Button>
            <Button variant="contained" onClick={onWhatsAppConnect} disabled={whatsAppLoader || !CommonHelper.HasProfessionalPlan()}>
              {whatsAppLoader ? <CircularProgress color="inherit" size={18} /> : 'Add WhatsApp Number'}
            </Button>
          </Stack>
        </Stack>

        <Tabs value={accountTab} onChange={(_event, value) => setAccountTab(value)}>
          <Tab value="whatsapp" label={`WhatsApp (${whatsAppAccounts.length})`} />
          <Tab value="facebook" label={`Facebook (${facebookAccounts.length})`} />
          <Tab value="instagram" label={`Instagram (${instagramAccounts.length})`} />
        </Tabs>

        {accountTab === 'whatsapp' && (
          !CommonHelper.HasProfessionalPlan() && <Typography color="text.secondary" variant="body2">
            {i18n.t('plan_access.whatsapp_upgrade')}
          </Typography>
        )}
        {accountTab === 'whatsapp' && (
          <WhatsAppAccounts
            accounts={whatsAppAccounts}
            isLoading={whatsAppLoader || connectedProfilesLoader}
            onConnect={onWhatsAppConnect}
            onReauthenticate={onWhatsAppReauthenticate}
            onRefresh={onRefresh}
            refreshingAccount={refreshingAccount}
            onDelete={onDelete}
            onToggleEnable={onToggleEnable}
          />
        )}
        {accountTab === 'facebook' && (
          <FacebookAccounts
            accounts={facebookAccounts}
            isLoading={facebookLoader || connectedProfilesLoader}
            onConnect={onFacebookConnect}
            onReauthenticate={onFacebookReauthenticate}
            onRefresh={onRefresh}
            refreshingAccount={refreshingAccount}
            onDelete={onDelete}
            onToggleEnable={onToggleEnable}
          />
        )}
        {accountTab === 'instagram' && (
          <InstagramAccounts
            accounts={instagramAccounts}
            isLoading={instagramLoader || connectedProfilesLoader}
            onConnect={onInstagramConnect}
            onReauthenticate={onInstagramReauthenticate}
            onRefresh={onRefresh}
            refreshingAccount={refreshingAccount}
            onDelete={onDelete}
            onToggleEnable={onToggleEnable}
          />
        )}
      </Stack>
    </Card>
  );
}
